import { useQuery } from 'react-query';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import datetimeHelper from 'helpers/datetimeHelper';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';

import type { ComplianceOverviewSummary } from '../../_types/risk_pool';

interface QueryParams {
  date: string;
  search?: string;
  compliance?: string;
}

// Generate mock data from organizations for demo purposes
const generateMockData = (organizations: any[]): ComplianceOverviewSummary => {
  return {
    compliant: organizations.filter((org) => org.compliancePerformance === 'compliant').length,
    partial: organizations.filter((org) => org.compliancePerformance === 'partial').length,
    nonCompliant: organizations.filter((org) => org.compliancePerformance === 'nonCompliant').length,
    total: organizations.length,
  };
};

export const getComplianceOverview = async (
  orgId: string,
  params: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ComplianceOverviewSummary> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/risk-pool/policy/compliance-overview`;

  const apiParams = {
    date: params.date,
    search: params.search,
    compliance: params.compliance,
  };

  const paramString = createParamString(apiParams);

  try {
    const apiData = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return apiData;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useComplianceOverview(
  orgId: string,
  params: QueryParams,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const isDateValid = datetimeHelper.isValidDateString(params.date);
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<ComplianceOverviewSummary, Error>(
    ['compliance_overview', orgId, params.date, params.search, params.compliance],
    async ({ signal }): Promise<ComplianceOverviewSummary> => {
      if (isDemoDataEnabled) {
        const organizations = generateRiskPoolMetrics(orgId, params);
        return generateMockData(organizations);
      }
      return getComplianceOverview(orgId, params, signal, onError);
    },
    {
      ...options,
      enabled: !!orgId && isDateValid,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: isDemoDataEnabled ? false : 1000 * 60 * 2,
    }
  );
}
